import { computed, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { buildRequestErrorMessage, formatFileSize, getPathFileName, isWindowsAbsolutePath } from './shared';

const DOWNLOAD_LIMIT_ROOT = 'C:\\mazda';
const DEFAULT_EXE_PATH = 'C:\\mazda\\tool_MAZDA.exe';
const DEFAULT_OTHER_DIR = 'C:\\mazda';

function normalizeWindowsPath(path) {
  return String(path || '')
    .replace(/\//g, '\\')
    .replace(/\\+/g, '\\')
    .trim()
    .replace(/\\+$/, '')
    .toLowerCase();
}

function isPathUnderDownloadRoot(path) {
  const normalizedPath = normalizeWindowsPath(path);
  const normalizedRoot = normalizeWindowsPath(DOWNLOAD_LIMIT_ROOT);
  return normalizedPath === normalizedRoot || normalizedPath.startsWith(`${normalizedRoot}\\`);
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'download.bin';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function tryReadBlobError(blob) {
  try {
    if (!(blob instanceof Blob)) return '';
    const text = await blob.text();
    if (!text) return '';
    try {
      const json = JSON.parse(text);
      return json?.message || json?.detail || text;
    } catch (_) {
      return text;
    }
  } catch (_) {
    return '';
  }
}

function defaultPathForKind(kind, fileName) {
  if (kind === 'exe') return DEFAULT_EXE_PATH;
  if (fileName) return `${DEFAULT_OTHER_DIR}\\${fileName}`;
  return `${DEFAULT_OTHER_DIR}\\`;
}

export function useMazdaReplace(mazdaPost) {
  const uploadKind = ref('exe');
  const targetReplaceFilePath = ref(DEFAULT_EXE_PATH);
  const editPath = ref('');
  const dialogExchangeFileName = ref(false);
  const uploadfiletest = ref(null);
  const oldFile = ref(null);
  const loading = ref(false);
  const queryFileSizeLoading = ref(false);
  const downloadLoading = ref(false);
  const rollbackLoading = ref(false);
  const targetFileSizeQueried = ref(false);
  const targetFileExists = ref(false);
  const targetFileSizeBytes = ref(0);
  const targetFileQueryMessage = ref('');
  const backupExists = ref(false);
  const backupSizeBytes = ref(0);

  const acceptByKind = computed(() =>
    uploadKind.value === 'exe'
      ? '.exe'
      : '.xlsx,.xls,.json,.txt,.bin,.zip,.xml,.csv,.dat,.pem,.crt,.key',
  );
  const canRollback = computed(() => uploadKind.value === 'exe');
  const pathEditable = computed(() => uploadKind.value === 'other');

  const resetQueryState = () => {
    targetFileSizeQueried.value = false;
    targetFileExists.value = false;
    targetFileSizeBytes.value = 0;
    targetFileQueryMessage.value = '';
    backupExists.value = false;
    backupSizeBytes.value = 0;
  };

  watch(targetReplaceFilePath, resetQueryState);
  watch(uploadKind, (kind) => {
    targetReplaceFilePath.value = defaultPathForKind(kind, oldFile.value?.name);
    resetQueryState();
  });

  const showReplaceFileDialog = () => {
    editPath.value = targetReplaceFilePath.value;
    dialogExchangeFileName.value = true;
  };
  const confirmReplaceFile = () => {
    targetReplaceFilePath.value = editPath.value;
    dialogExchangeFileName.value = false;
  };
  const cancelInputReplacePath = () => {
    dialogExchangeFileName.value = false;
  };

  const replaceFile = (file) => {
    oldFile.value = file.raw;
    if (String(oldFile.value?.name || '').toLowerCase().endsWith('.dll')) {
      oldFile.value = null;
      uploadfiletest.value?.clearFiles();
      ElMessage.error('DLL 必须在“DLL 安全热更新”页面上传');
      return;
    }
    if (uploadKind.value === 'other' && oldFile.value?.name) {
      const current = String(targetReplaceFilePath.value || '').replace(/[\\/]+$/, '');
      const currentName = getPathFileName(current);
      if (!current || current.toLowerCase() === DEFAULT_OTHER_DIR.toLowerCase() || !currentName) {
        targetReplaceFilePath.value = `${DEFAULT_OTHER_DIR}\\${oldFile.value.name}`;
      } else if (isPathUnderDownloadRoot(current) && currentName !== oldFile.value.name) {
        const parent = current.slice(0, current.length - currentName.length).replace(/[\\/]+$/, '');
        targetReplaceFilePath.value = `${parent || DEFAULT_OTHER_DIR}\\${oldFile.value.name}`;
      }
    }
  };

  const replaceFileTest = (files) => {
    ElMessage.warning(`最多只能上传1个文件，您选择了${files.length}个文件。`);
  };
  const clearReplaceFile = () => {
    oldFile.value = null;
    uploadfiletest.value?.clearFiles();
  };

  const ensureValidTargetPath = () => {
    if (!targetReplaceFilePath.value) {
      ElMessage.warning('请输入目标文件完整路径');
      return false;
    }
    if (!isWindowsAbsolutePath(targetReplaceFilePath.value)) {
      ElMessage.error('目标文件路径必须是完整绝对路径');
      return false;
    }
    if (!isPathUnderDownloadRoot(targetReplaceFilePath.value)) {
      ElMessage.error(`目标文件只能位于 ${DOWNLOAD_LIMIT_ROOT} 目录下`);
      return false;
    }
    return true;
  };

  const applyQueryData = (data) => {
    targetFileSizeQueried.value = true;
    targetFileExists.value = !!data.exists;
    targetFileSizeBytes.value = Number(data.size || 0);
    targetFileQueryMessage.value = data.message || '';
    backupExists.value = !!data.backupExists;
    backupSizeBytes.value = Number(data.backupSize || 0);
  };

  const queryCurrentTargetFileSize = async () => {
    if (!ensureValidTargetPath()) return;
    queryFileSizeLoading.value = true;
    try {
      const response = await mazdaPost('queryTargetFileSize', {
        targetPath: targetReplaceFilePath.value,
      }, { timeout: 15000 });
      const data = response?.data || {};
      applyQueryData(data);
      if (data.success) {
        ElMessage[data.exists ? 'success' : 'warning'](
          data.exists ? `当前目标文件大小：${formatFileSize(Number(data.size || 0))}` : (data.message || '目标文件不存在'),
        );
      } else {
        ElMessage.error(data.message || '查询目标文件大小失败');
      }
    } catch (error) {
      targetFileSizeQueried.value = false;
      ElMessage.error(buildRequestErrorMessage('查询目标文件大小失败', error));
    } finally {
      queryFileSizeLoading.value = false;
    }
  };

  const downloadTargetFile = async () => {
    if (!ensureValidTargetPath()) return;
    downloadLoading.value = true;
    try {
      const response = await mazdaPost('downloadTargetFile', {
        targetPath: targetReplaceFilePath.value,
      }, { timeout: 60000, responseType: 'blob' });
      const blob = response?.data;
      const contentType = response?.headers?.['content-type'] || '';
      if (String(contentType).includes('application/json')) {
        ElMessage.error((await tryReadBlobError(blob)) || '下载目标文件失败');
        return;
      }
      triggerBlobDownload(blob, getPathFileName(targetReplaceFilePath.value) || 'download.bin');
      ElMessage.success('目标文件下载成功');
    } catch (error) {
      let message = buildRequestErrorMessage('下载目标文件失败', error);
      if (error?.response?.data instanceof Blob) {
        const detail = await tryReadBlobError(error.response.data);
        if (detail) message = `下载目标文件失败: ${detail}`;
      }
      ElMessage.error(message);
    } finally {
      downloadLoading.value = false;
    }
  };

  const submitUpload = async () => {
    if (!oldFile.value) {
      ElMessage.warning('请先选择要上传替换的文件');
      return;
    }
    if (!ensureValidTargetPath()) return;

    const uploadName = String(oldFile.value.name || '');
    if (uploadName.toLowerCase().endsWith('.dll')) {
      ElMessage.error('DLL 必须在“DLL 安全热更新”页面上传');
      return;
    }
    if (uploadKind.value === 'exe' && !uploadName.toLowerCase().endsWith('.exe')) {
      ElMessage.error('EXE 上传只能选择 .exe 文件');
      return;
    }
    const targetName = getPathFileName(targetReplaceFilePath.value);
    if (!targetName || targetName !== uploadName) {
      ElMessage.error('目标文件名必须与上传文件名一致');
      return;
    }

    loading.value = true;
    try {
      const formData = new FormData();
      formData.append('file', oldFile.value);
      formData.append('targetPath', targetReplaceFilePath.value);
      formData.append('kind', uploadKind.value);
      const response = await mazdaPost('replaceFile', formData, { timeout: 120000 });
      const data = response?.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '文件替换失败');
        return;
      }
      ElMessage.success(data.message || '文件替换成功');
      applyQueryData(data);
      targetFileExists.value = true;
      targetFileSizeBytes.value = Number(oldFile.value.size || data.size || 0);
      clearReplaceFile();
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('文件替换失败', error));
    } finally {
      loading.value = false;
    }
  };

  const rollbackCurrent = async () => {
    if (!canRollback.value) return;
    try {
      await ElMessageBox.confirm('确认回滚到上一份 EXE？会同步对端服务器。', '回滚', { type: 'warning' });
    } catch (_) {
      return;
    }
    rollbackLoading.value = true;
    try {
      const response = await mazdaPost('rollbackFile', {
        kind: 'exe',
        targetPath: targetReplaceFilePath.value,
      });
      const data = response?.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '回滚失败');
        return;
      }
      applyQueryData(data);
      ElMessage.success(data.message || '已回滚');
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('回滚失败', error));
    } finally {
      rollbackLoading.value = false;
    }
  };

  return {
    uploadKind,
    acceptByKind,
    canRollback,
    pathEditable,
    targetReplaceFilePath,
    editPath,
    dialogExchangeFileName,
    uploadfiletest,
    oldFile,
    loading,
    queryFileSizeLoading,
    downloadLoading,
    rollbackLoading,
    targetFileSizeQueried,
    targetFileExists,
    targetFileSizeBytes,
    targetFileQueryMessage,
    backupExists,
    backupSizeBytes,
    formatFileSize,
    replaceFile,
    replaceFileTest,
    clearReplaceFile,
    showReplaceFileDialog,
    confirmReplaceFile,
    cancelInputReplacePath,
    queryCurrentTargetFileSize,
    downloadTargetFile,
    submitUpload,
    rollbackCurrent,
  };
}
